import React, { useEffect, useState } from 'react';
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { Space, Tag, Tooltip, InputNumber } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { CourseTable } from '@/domains/course/entities';
import { OperationMethodTable } from '@/domains/operation-methods/entities';
import { GetListOperationMethod } from '@/modules/operation-methods/viewmodels';
import { CoreInput } from '@/components/Form/InputAntForm';
import CoreSelect from '@/components/Form/SelectAntForm';
import { useCoreContext } from '@/core';

interface TableProps {
  onSelectItem: (id: string) => void;
  onRemoveItem?: (id: string) => void;
}

const TableColumn = ({ onSelectItem, onRemoveItem }: TableProps) => {
  const [operationMethods, setOperationMethods] = useState<OperationMethodTable[]>([]);
  const { operationMethodData, actionGetAllOperationMethod } = GetListOperationMethod();
  const { control, setValue } = useCoreContext();

  useEffect(() => {
    actionGetAllOperationMethod();
  }, []);

  useEffect(() => {
    if (operationMethodData) {
      setOperationMethods(operationMethodData);
    }
  }, [operationMethodData]);

  // Helper function to get operation method name from ID
  const getOperationMethodName = (id: string) => {
    const method = operationMethods.find((method) => method.id === id);
    return method ? method.name : id;
  };

  const optionsStatus = [
    { value: true, label: 'ACTIVE' },
    { value: false, label: 'INACTIVE' },
  ];

  const getTextFilterProps = (fieldName: string): ColumnType<CourseTable> => ({
    filterDropdown: () => (
      <div style={{ padding: 8, width: '200px' }} onKeyDown={(e) => e.stopPropagation()}>
        <CoreInput control={control} name={fieldName} />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  });

  const getNumberFilterProps = (fieldName: string): ColumnType<CourseTable> => ({
    filterDropdown: () => (
      <div style={{ padding: 8, width: '200px' }} onKeyDown={(e) => e.stopPropagation()}>
        <InputNumber
          placeholder='Enter value'
          style={{ width: '100%' }}
          onChange={(value) => {
            setValue(fieldName, value);
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  });

  const getStatusFilterProps = (): ColumnType<CourseTable> => ({
    filterDropdown: () => (
      <div style={{ padding: 8, width: '200px' }} onKeyDown={(e) => e.stopPropagation()}>
        <CoreSelect control={control} name='isActive' optionsProps={optionsStatus} />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  });

  const columns: ColumnsType<CourseTable> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getTextFilterProps('search'),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Short Name',
      dataIndex: 'shortName',
      key: 'shortName',
      ...getTextFilterProps('shortName'),
    },
    {
      title: 'Number of Session',
      dataIndex: 'numberOfSession',
      key: 'numberOfSession',
      ...getNumberFilterProps('numberOfSession'),
    },
    {
      title: 'Session Hour',
      dataIndex: 'sessionHour',
      key: 'sessionHour',
      ...getNumberFilterProps('sessionHour'),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <>
          {isActive ? (
            <Tag icon={<CheckCircleOutlined />} color='success'>
              Active
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color='error'>
              Inactive
            </Tag>
          )}
        </>
      ),
      ...getStatusFilterProps(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size='middle'>
          <Tooltip title='Edit'>
            <EditOutlined
              className='text-primary'
              onClick={() => onSelectItem(record.id)}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
          {onRemoveItem && (
            <Tooltip title='Delete'>
              <DeleteOutlined
                className='text-danger'
                onClick={() => onRemoveItem(record.id)}
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return columns;
};

export default TableColumn;
